import { t } from "i18next";
import { useContext, useMemo } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Tooltip } from "antd";
import { Table } from "../../../../../components/Table/Table";
import {
  renderOznakaSaKolicinomPoRM,
  renderOznakaSaKolicinomSerijskiBrojevi,
  renderOznakeSaKolicinom,
} from "../../../../../modules/obligee/periodicReports/periodicReport.functions";
import { PeriodicReportsContext } from "../../../../../modules/obligee/periodicReports/periodicReports.context";
import {
  TobaccoReport,
  TobaccoReportType,
} from "../../../../../modules/obligee/periodicReports/periodicReports.types";
import { useScreenResize } from "../../../../../utils/useScreenResize";

interface Props {
  type: TobaccoReportType;
}

export const TobaccoTable = ({ type }: Props) => {
  const { periodicReports } = useContext(PeriodicReportsContext);
  const { width } = useScreenResize();
  const colDefTobacco = [
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_I_DATUM_ZAMD"),
      children: [
        {
          title: "1",
          width: "240px",
          fixed: "left",
          dataIndex: "brojIDatumZahteva",
        },
      ],
    },
    {
      title: t("PERIODICNI_IZVESTAJI.SLOVNA_OZNAKA_I_KOLICINA"),
      children: [
        {
          title: "2",
          fixed: "left",
          width: "240px",
          dataIndex: "slovnaOznakaIKolicinaPreuzetihMarkica",
          render: (_: string, record: TobaccoReport) => (
            <Flex gap={10} vertical>
              {record.slovnaOznakaIKolicinaPreuzetihMarkica?.map((oznaka) => {
                return oznaka ? <span>{oznaka}</span> : <br />;
              })}
            </Flex>
          ),
        },
      ],
    },
    {
      title: t("PERIODICNI_IZVESTAJI.OZNAKA_SERIJE_I_SERIJSKI_BROJ"),
      children: [
        {
          title: "3",
          width: "240px",
          fixed: "left",
          dataIndex: "oznakaSerijeISerijskiBrojPreuzetihMarkica",
          render: (_: string, record: TobaccoReport) => (
            <Flex gap={10} vertical>
              {record.oznakaSerijeISerijskiBrojPreuzetihMarkica?.map(
                (oznaka) => {
                  return oznaka ? <span>{oznaka}</span> : <br />;
                }
              )}
            </Flex>
          ),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_4")}>
          {t("PERIODICNI_IZVESTAJI.STANJE_NERAZDUZENIH_MARKICA")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "4",
          width: "240px",
          render: (record: TobaccoReport) =>
            renderOznakeSaKolicinom(
              record.brojNeutrosenihMarkicaPrethodniKvartal
            ),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_5_DUVAN")}>
          {t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_DUVAN")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "5",
          width: "240px",
          render: (record: TobaccoReport) =>
            renderOznakeSaKolicinom(
              record.brojIskoriscenihMarkicaIzProizvodnje
            ),
        },
      ],
    },
    {
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_6")}>
          {t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_PROMET_DUVAN")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: "240px",
      children: [
        {
          title: "6",
          width: "240px",
          render: (record: TobaccoReport) =>
            renderOznakeSaKolicinom(record.brojIskoriscenihMarkicaIzUvoza),
        },
      ],
    },
    {
      width: "240px",
      title: t("PERIODICNI_IZVESTAJI.NAZIV_ROBNE_MARKE_CIGARETA"),
      children: [
        {
          title: "7",
          width: "280px",
          render: (record: TobaccoReport) =>
            renderOznakaSaKolicinomPoRM(
              record.nazivRobneMarkeIMpcUvoz,
              t("PERIODICNI_IZVESTAJI.RSD")
            ),
        },
      ],
    },
    {
      width: "240px",
      title: (
        <Tooltip title={t("PERIODICNI_IZVESTAJI.NAPOMENA_KOLONA_8")}>
          {t("PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA")}{" "}
          <InfoCircleOutlined />
        </Tooltip>
      ),
      children: [
        {
          title: "8",
          width: "240px",
          render: (record: TobaccoReport) =>
            renderOznakeSaKolicinom(record.brojOstecenihVidljivihMarkica),
        },
      ],
    },
    {
      width: "240px",
      title: t(
        "PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA_NEVIDLJIVI"
      ),
      children: [
        {
          title: "9",
          width: "240px",
          render: (record: TobaccoReport) =>
            renderOznakeSaKolicinom(
              record.brojOstecenihNevidljivihMarkica,
              true
            ),
        },
      ],
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE"),
      width: "240px",
      children: [
        {
          title: "10",
          width: "260px",
          render: (record: TobaccoReport) =>
            renderOznakaSaKolicinomSerijskiBrojevi(
              record.brojNerazduzenihMarkicaISerijskiBrojNaKraju
            ),
        },
      ],
    },
  ];

  const tableTitle = useMemo(() => {
    switch (type) {
      case TobaccoReportType.CIGARETE:
        return t("AKCIZNE_MARKICE.CIGARETE");
      case TobaccoReportType.NESAGOREVAJUCI:
        return t("AKCIZNE_MARKICE.NESAGOREVAJUCI_DUVAN");
      default:
        return t("AKCIZNE_MARKICE.DUVANSKI_PROIZVODI");
    }
  }, [type]);

  const tableData = useMemo(() => {
    switch (type) {
      case TobaccoReportType.CIGARETE:
        return periodicReports?.tobaccoReport?.cigareteReport;
      case TobaccoReportType.NESAGOREVAJUCI:
        return periodicReports?.tobaccoReport?.nesagorevajuciReport;
      default:
        return [];
    }
  }, [
    type,
    periodicReports?.tobaccoReport?.cigareteReport,
    periodicReports?.tobaccoReport?.nesagorevajuciReport,
  ]);

  const scrollConfig =
    width < 992
      ? undefined
      : [".ant-tabs-nav", "#periodic", "#tobacco_table_header"];

  return (
    <div>
      <div id="tobacco_table_header" style={{ display: "flex" }}>
        <h2 className="alignCenter">{tableTitle?.toUpperCase()}</h2>
      </div>
      <Table
        scrollConfig={scrollConfig}
        columns={colDefTobacco}
        dataSource={tableData}
        name="periodicni-izvestaji-tabela-duvan"
      />
    </div>
  );
};
